import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Box, Flex } from "@chakra-ui/react";
import { FaCheck, FaKey, FaList, FaPrint, } from "react-icons/fa";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import SearchTable2 from "components/Tables/SearchTable2";
import Loading from "components/Loading/Loading";
import ConfirmDialog from "components/Dialog/ConfirmDialog";
import { CustomProgressBar } from "components/CustomProgressBar/CustomProgressBar";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { violationAllotmentsValidationService, AllotmentStatus } from "services/violationAllotmentsValidationService";
import { getEmptyPageResponse } from "utils";
import { PERMISSIONS } from "constants/permission";
import { ROUTES } from "constants/routes-path";

const ViolationAllotmentsValidationList = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useState({orderBy: "quantidade_dias_data_limite"});
  const [response, setResponse] = useState(undefined);
  const [selectedRow, setSelectedRow] = useState(undefined);
  const dialogRef = useRef(undefined);
  const navigate = useNavigate();
  const showToast = useCustomToast();

  const translationKey = "violation-allotments.validation.list";
  const [isLoadingPrintAit, setIsLoadingPrintAit] = useState(false);

  const columns = useMemo(() => ([
    { Header: "", accessor: "id", isHidden: true },
    { Header: "", accessor: "quantidade", isHidden: true },
    { Header: "", accessor: "quantidade_validada", isHidden: true },
    { Header: "", accessor: "quantidade_nao_validada", isHidden: true },
    { Header: t(`${translationKey}.table.header.name`), accessor: "nome" },
    {
      Header: t(`${translationKey}.table.header.progress`),
      disableSortBy: true,
      custom: ({ row }) => (<CustomProgressBar current={row.original.quantidade_validada ?? 0} total={row.original.quantidade ?? 0} />),
    },
    {
      Header: t(`${translationKey}.table.header.creation-date`),
      accessor: "data_criacao",
      custom: ({ value }) => value ?? "-",
    },
    {
      Header: t(`${translationKey}.table.header.days-remaining`),
      accessor: "quantidade_dias_data_limite",
      custom: ({ value }) => {
        const label = value === 1 ? "singular" : "plural";
        return value ? t(`system.labels.days.${label}`, { days: value }) : "-";
      },
    },
    {
      Header: t("system.labels.actions"),
      disableSortBy: true,
      actions: [
        {
          label: t(`${translationKey}.actions.list-violations`),
          permission: PERMISSIONS.VIOLATION_VIEW,
          icon: <FaList />,
          onClick: ({ original }) => {
            const validatePath = ROUTES.violationAllotmentsViolations.replace(":allotmentId", original.id);
            navigate(ROUTES.transportManagementPath + validatePath);
          },
        },
        {
          label: t(`${translationKey}.actions.close`),
          permission: PERMISSIONS.VIOLATION_VALIDATION,
          icon: <FaKey />,
          visible: (row) => row.quantidade_nao_validada === 0,
          onClick: ({ original }) => handleCloseAllotment(original),
        },
        {
          label: t(`${translationKey}.actions.print-aits`),
          permission: PERMISSIONS.VIOLATION_VALIDATION,
          icon: <FaPrint />,
          visible: (row) => row.quantidade_nao_validada === 0,
          onClick: ({ original }) => handlePrintAit(original.id),
        },
        {
          label: t(`${translationKey}.actions.validate`),
          permission: PERMISSIONS.VIOLATION_VALIDATION,
          icon: <FaCheck />,
          visible: (row) => row.quantidade_nao_validada > 0,
          onClick: ({ original }) => {
            const validatePath = ROUTES.violationAllotmentsValidate.replace(":allotmentId", original.id);
            navigate(ROUTES.transportManagementPath + validatePath);
          },
        },
      ],
    }
  ]), []);

  const handleCloseAllotment = (row) => {
    setSelectedRow(row);
    dialogRef.current?.displayDialog({
      title: t(`${translationKey}.actions.close`),
      message: t(`${translationKey}.actions.close-confirm`, { allotmentName: row.nome, interpolation: { escapeValue: false } }),
    });
  };

  const handleConfirmCloseAllotment = async () => {
    try {
      await violationAllotmentsValidationService.updateStatus(selectedRow.id, AllotmentStatus.CLOSED.value);
      showToast({ title: t("violation-allotments.messages.success.allotment-closed"), status: "success" });
      await fetchData();
    } catch (error) {
      showToast({ title: t(error), status: "error" });
    }
  };

  const handlePrintAit = async (id) => {
    try {
      setIsLoadingPrintAit(true);

      const { data, headers } = await violationAllotmentsValidationService.printAit(id);
      const fileName = headers['content-disposition'].match(/filename="(.+)"/)[1];

      const url = URL.createObjectURL(new Blob([data]));
      Object.assign(document.createElement('a'), {
        href: url,
        download: fileName
      }).click();

      URL.revokeObjectURL(url);
    } catch (error) {
      showToast({ title: t(error), status: "error" });
    } finally {
      setIsLoadingPrintAit(false);
    }
  };

  const fetchData = async (params = {}) => {
    if (response) setResponse(undefined);
    try {
      params = { ...searchParams, ...params };
      setSearchParams(params);
      const data = await violationAllotmentsValidationService.list(params);
      setResponse(data);
    } catch (err) {
      setResponse(getEmptyPageResponse(searchParams));
      showToast({ title: t(err), status: "error" });
    }
  }

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <Flex direction="column" gap="5" pt={{ base: "150px", lg: "90px" }}>
      <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
        <ListPageCardHeader title={t("violation-allotments.title")} subtitle={t("violation-allotments.description")} />
        <CardBody>
          {response ? (
            <Box position="relative">
              <SearchTable2
                  columnsData={columns}
                  tableData={response?.content ?? []}
              />
              {(isLoadingPrintAit &&
                <Box
                  position="fixed"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  bg="rgba(255, 255, 255, 0.6)"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  zIndex="overlay"
                >
                  <Loading maxH={{ sm: "100px", md: "350px" }} />
                </Box>
              )}
            </Box>
          ) : (
            <Loading maxH={{ sm: "100px", md: "350px" }} />
          )}
        </CardBody>
      </Card>

      <ConfirmDialog ref={dialogRef} onConfirm={handleConfirmCloseAllotment} />
    </Flex>
  );
}

export default ViolationAllotmentsValidationList;
